// Practice Projects Utility Functions
// Date: January 21, 2026

import type {
  DifficultyLevel,
  ProjectStatus,
  EnrollmentStatus,
  PracticeProject,
} from '../types/practiceProjects';

/**
 * Get badge color for difficulty level
 */
export function getDifficultyColor(level: DifficultyLevel): string {
  switch (level) {
    case 'beginner':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'intermediate':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'advanced':
      return 'bg-red-100 text-red-800 border-red-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

/**
 * Get badge color for project status
 */
export function getStatusColor(status: ProjectStatus): string {
  switch (status) {
    case 'published':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'draft':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'archived':
      return 'bg-orange-100 text-orange-800 border-orange-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

/**
 * Get badge color for enrollment status
 */
export function getEnrollmentStatusColor(status: EnrollmentStatus): string {
  switch (status) {
    case 'not_started':
      return 'bg-gray-100 text-gray-800 border-gray-200';
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'completed':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

/**
 * Format difficulty level to display text
 */
export function formatDifficulty(level: DifficultyLevel): string {
  switch (level) {
    case 'beginner':
      return 'Beginner';
    case 'intermediate':
      return 'Intermediate';
    case 'advanced':
      return 'Advanced';
    default:
      return level;
  }
}

/**
 * Format status to display text
 */
export function formatStatus(status: ProjectStatus): string {
  switch (status) {
    case 'published':
      return 'Published';
    case 'draft':
      return 'Draft';
    case 'archived':
      return 'Archived';
    default:
      return status;
  }
}

/**
 * Format enrollment status to display text
 */
export function formatEnrollmentStatus(status: EnrollmentStatus): string {
  switch (status) {
    case 'not_started':
      return 'Not Started';
    case 'in_progress':
      return 'In Progress';
    case 'completed':
      return 'Completed';
    default:
      return status;
  }
}

/**
 * Calculate progress bar color based on completion percentage
 */
export function getProgressColor(percentage: number): string {
  if (percentage === 0) return 'bg-gray-200';
  if (percentage < 25) return 'bg-red-500';
  if (percentage < 50) return 'bg-orange-500';
  if (percentage < 75) return 'bg-yellow-500';
  if (percentage < 100) return 'bg-blue-500';
  return 'bg-green-500';
}

/**
 * Format completion percentage to display text
 */
export function formatCompletionPercentage(percentage: number): string {
  return `${Math.round(percentage)}%`;
}

/**
 * Get estimated time based on difficulty
 */
export function getEstimatedTime(level: DifficultyLevel): string {
  switch (level) {
    case 'beginner':
      return '2-4 hours';
    case 'intermediate':
      return '4-8 hours';
    case 'advanced':
      return '8-16 hours';
    default:
      return 'Unknown';
  }
}

/**
 * Check if user can edit project
 */
export function canEditProject(project: PracticeProject, userId: string): boolean {
  return project.created_by === userId && project.status !== 'archived';
}

/**
 * Check if user can publish project
 */
export function canPublishProject(project: PracticeProject, userId: string): boolean {
  return (
    project.created_by === userId &&
    project.status === 'draft' &&
    !!project.title &&
    !!project.description &&
    !!project.difficulty_level
  );
}

/**
 * Check if user can enroll in project
 */
export function canEnrollInProject(project: PracticeProject): boolean {
  return project.status === 'published' && !project.enrollmentStatus?.isEnrolled;
}

/**
 * Truncate text with ellipsis
 */
export function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
}

/**
 * Parse HTML to plain text (for descriptions)
 */
export function htmlToPlainText(html: string): string {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

/**
 * Validate Q&A item data
 */
export function validateQAItem(data: {
  question: string;
  answer: string;
  explanation: string;
}): { isValid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data.question || data.question.trim().length === 0) {
    errors.push('Question is required');
  }
  if (!data.answer || data.answer.trim().length === 0) {
    errors.push('Answer is required');
  }
  if (!data.explanation || data.explanation.trim().length === 0) {
    errors.push('Explanation is required');
  }

  return { isValid: errors.length === 0, errors };
}

/**
 * Sort Q&A items by order index
 */
export function sortQAItems<T extends { order_index: number }>(items: T[]): T[] {
  return [...items].sort((a, b) => a.order_index - b.order_index);
}

/**
 * Generate unique order index for new Q&A item
 */
export function getNextOrderIndex<T extends { order_index: number }>(items: T[]): number {
  if (items.length === 0) return 0;
  return Math.max(...items.map((item) => item.order_index)) + 1;
}

/**
 * Format date to relative time (e.g., "2 days ago")
 */
export function formatRelativeTime(date: string | Date): string {
  const now = new Date();
  const targetDate = typeof date === 'string' ? new Date(date) : date;
  const diffMs = now.getTime() - targetDate.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `${years} year${years > 1 ? 's' : ''} ago`;
}

/**
 * Validate project form data
 */
export function validateProjectForm(data: {
  title: string;
  description: string;
  difficulty_level: DifficultyLevel | '';
}): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {};

  if (!data.title || data.title.trim().length === 0) {
    errors.title = 'Title is required';
  } else if (data.title.length > 200) {
    errors.title = 'Title must be less than 200 characters';
  }

  if (!data.description || data.description.trim().length === 0) {
    errors.description = 'Description is required';
  }

  if (!data.difficulty_level) {
    errors.difficulty_level = 'Difficulty level is required';
  }

  return { isValid: Object.keys(errors).length === 0, errors };
}

/**
 * Extract plain text from HTML and limit length
 */
export function getExcerpt(html: string, maxLength: number = 150): string {
  const plainText = htmlToPlainText(html);
  return truncateText(plainText, maxLength);
}

/**
 * Check if array contains any search term
 */
export function arrayContainsSearch(arr: string[], searchTerm: string): boolean {
  const lowerSearch = searchTerm.toLowerCase();
  return arr.some((item) => item.toLowerCase().includes(lowerSearch));
}

/**
 * Format resource profile display
 */
export function formatResourceProfile(profile: {
  cpu_cores: number;
  ram_gb: number;
  disk_gb: number;
}): string {
  return `${profile.cpu_cores} CPU, ${profile.ram_gb}GB RAM, ${profile.disk_gb}GB Disk`;
}
